import isTimeStringValid from '@jira/issue__get-formatted-time-string/src/is-time-string-valid/index.tsx';
import secondsToTimeString from '@jira/issue__get-formatted-time-string/src/seconds-to-time-string/index.tsx';
import timeStringToSeconds from '@jira/issue__get-formatted-time-string/src/time-string-to-seconds/index.tsx';
import type { TimeTrackingConfig } from '@atlassian/jira-issue-shared-types/src/common/types/jira-settings-type.tsx';
import { getDefaultTimeTrackingUnit } from './common/utils/default-time-tracking-unit/index.tsx';

const isNumeric = (num: string): boolean => num.length > 0 && !Number.isNaN(Number(num));

export const formatTimeString =
	(config: TimeTrackingConfig) =>
	(timeValue: number | null): string =>
		secondsToTimeString(config.daysPerWeek, config.hoursPerDay)(timeValue || 0);

const appendTimeUnitIfMissing =
	(config: TimeTrackingConfig) =>
	(value: string): string => {
		let trimmedValue = value.trim();

		// If user doesn't type the valid format (m, h, d, or w), use default unit format set in the configuration.
		const isValidNumber = isNumeric(trimmedValue.replaceAll(',', '.'));
		if (isValidNumber) {
			const { defaultUnit } = config;
			trimmedValue = `${trimmedValue}${getDefaultTimeTrackingUnit(defaultUnit)}`;
		}

		return trimmedValue;
	};

export const isValidTimeString =
	(config: TimeTrackingConfig) =>
	(value: string): boolean => {
		const timeString = appendTimeUnitIfMissing(config)(value);
		return timeString === '' || isTimeStringValid(timeString);
	};

export const parseTimeString =
	(config: TimeTrackingConfig) =>
	(value: string): number | null => {
		const timeString = appendTimeUnitIfMissing(config)(value);

		if (!isValidTimeString(config)(timeString)) {
			return 0;
		}

		return timeStringToSeconds(config.daysPerWeek, config.hoursPerDay)(timeString);
	};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { secondsToWholeMinuteTimeString } from './common/utils/seconds-to-whole-minute-time-string/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { TimeTrackingConfig };
